import * as THREE from 'three';
import { computeBeatPulse } from '../beat-pulse.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';

// Shared by all 3 Party Mode visualizers - not a Visualizer itself, just
// a self-contained scene-graph chunk with its own init/update/dispose,
// instantiated once per visualizer that wants it.
//
// Rendering approach: real SpotLights with shadow mapping were the
// obvious first idea, but that's exactly the kind of cost this pilot was
// warned to watch for (TV-class GPU, not desktop). What's actually used:
//   - a visible additive-blend cone mesh per head for the "beam you can
//     see" look (this is how most real-time concert-light fakes do it -
//     real productions don't ray-trace beams either)
//   - a non-shadow-casting SpotLight per head (castShadow: false) so the
//     beams also actually light up whatever's in the scene (particles,
//     geometry, floor) without the shadow-map cost
// If this still turns out too heavy on the real TV, the cones themselves
// are cheap (no shadows involved) - the SpotLights are the first thing to
// cut, the visible beams can carry the effect alone.

const DEFAULT_PALETTE = [0xff2fb0, 0x2fd8ff, 0xffb02f, 0x8f2fff];

// Beat-locked flash (see beat-pulse.js) applied uniformly across every
// head at once - the classic "lights hit on the downbeat" look, as one
// extra multiplier on top of everything else already driving cone/halo/
// spot intensity, so it's additive rather than a replacement for the
// existing cut/wander/palette behavior.
const BEAT_PULSE_AMOUNT = 0.55; // peak brightness boost right on the beat
const BEAT_PULSE_DECAY = 8; // higher = the flash narrows to right at the beat instead of filling it

export const HEAD_COUNT = 5;
const BLACKOUT_MS = 420; // total dip duration on a track change
// Wide enough that the heads' spread fills a 16:9 TV frame horizontally,
// not just a centered square - a narrower radius left large black margins
// on both sides at typical camera distance.
const RIG_RADIUS = 7;
const RIG_HEIGHT = 5.5;
const CONE_HEIGHT = 6;
// Fraction of the beam's length (from apex) where it starts fading to
// transparent, reaching zero right at the floor - avoids the beam ending
// in a hard, flat-looking disc where it lands (see LightHead's vertex
// color gradient below).
const FADE_START = 0.55;

// Per-head "cut" cycle timing (see LightHead._cutState / StageLightRig.
// _advanceCut). A continuous sweep - even a non-repeating, noise-driven
// one - still has *some* characteristic timescale a viewer can lock onto
// as a rhythm. Cutting each head to black and relighting it at a new
// target, on its own independently randomized schedule, has no shared
// cadence at all between heads, which is what actually reads as
// "jamais statique" rather than just "not exactly repeating".
const CUT_FADE_OUT_S = 0.18;
const CUT_FADE_IN_S = 0.26;

// See LightHead's `halos` array - a stack of this many thin, dimmer
// cones approximates a soft blurred edge instead of one visible second
// shape.
const HALO_LAYER_COUNT = 5;
const HALO_BASE_OPACITY = 0.05;

// More candidate mount points than there are heads (see buildMountSlots)
// so heads can actually relocate to a genuinely different spot - with
// only HEAD_COUNT slots, every head's *origin* (where the beam visibly
// starts) was pinned forever at one of 5 fixed points even though its
// aim varied, which is what read as "always starting from the same
// place". A head occasionally jumps to a different slot from this pool
// during a cut's blackout - see _advanceCut's relocation roll.
const MOUNT_SLOT_COUNT = 14;
const MOUNT_RELOCATE_CHANCE = 0.35;

function lerpHex(colorA, colorB, t, out) {
  out.copy(colorA).lerp(colorB, t);
  return out;
}

// A wider, slightly irregular pool of mount points around the rig -
// jittered radius/height/angle so it reads as scattered rigging rather
// than a second perfect ring. Rebuilt fresh each time StageLightRig
// initializes (each visualizer switch), so the available spots aren't
// the same across the whole session either.
function buildMountSlots(count) {
  const slots = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
    const radius = RIG_RADIUS + (Math.random() - 0.5) * 3;
    const height = RIG_HEIGHT + (Math.random() - 0.5) * 2;
    slots.push(new THREE.Vector3(Math.cos(angle) * radius, height, Math.sin(angle) * radius));
  }
  return slots;
}

// Per-vertex fade from full brightness at the apex to fully transparent
// at the base (used as the 'color' attribute; additive blending reads
// its intensity as brightness/opacity) - shared by the beam cone and its
// halo shell below, both built from the same cone shape at different radii.
function buildFadeColors(geometry, height) {
  const posAttr = geometry.attributes.position;
  const fadeColors = new Float32Array(posAttr.count * 3);
  for (let vi = 0; vi < posAttr.count; vi++) {
    const t = THREE.MathUtils.clamp(-posAttr.getY(vi) / height, 0, 1); // 0 apex, 1 base
    const intensity = t < FADE_START ? 1 : 1 - (t - FADE_START) / (1 - FADE_START);
    fadeColors[vi * 3] = intensity;
    fadeColors[vi * 3 + 1] = intensity;
    fadeColors[vi * 3 + 2] = intensity;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(fadeColors, 3));
}

// Shared hash+trilerp value noise (cheap - this is a small secondary
// detail blended at low contrast on small on-screen shapes, not a hero
// surface where grid artifacts would actually show).
const noiseGLSL = `
  float hash13(vec3 p3) {
    p3 = fract(p3 * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  float noise3D(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float n000 = hash13(i + vec3(0.0, 0.0, 0.0));
    float n100 = hash13(i + vec3(1.0, 0.0, 0.0));
    float n010 = hash13(i + vec3(0.0, 1.0, 0.0));
    float n110 = hash13(i + vec3(1.0, 1.0, 0.0));
    float n001 = hash13(i + vec3(0.0, 0.0, 1.0));
    float n101 = hash13(i + vec3(1.0, 0.0, 1.0));
    float n011 = hash13(i + vec3(0.0, 1.0, 1.0));
    float n111 = hash13(i + vec3(1.0, 1.0, 1.0));
    float nx00 = mix(n000, n100, f.x);
    float nx10 = mix(n010, n110, f.x);
    float nx01 = mix(n001, n101, f.x);
    float nx11 = mix(n011, n111, f.x);
    float nxy0 = mix(nx00, nx10, f.y);
    float nxy1 = mix(nx01, nx11, f.y);
    return mix(nxy0, nxy1, f.z);
  }
`;

// Tried displacing the cone's vertices radially by noise to break up its
// silhouette (real geometry, not just surface texture) - reverted: at
// any strength visible enough to matter it stopped reading as a beam at
// all ("des plantes qui dansent"). Back to the plain, undisplaced cone;
// the softening now comes from a separate blurred halo shell (see
// LightHead's `halo` mesh below) instead of deforming the beam itself.
// Shared by the core cone and the halo shells: fades alpha to zero right
// at the silhouette (where the surface is viewed edge-on, i.e. its
// normal is perpendicular to the view direction) instead of a hard
// geometric cutoff. The silhouette IS the grazing-angle region, so this
// blurs the actual visible *outline* of each shape - unlike the earlier
// density noise, which only broke up what's inside the shape and left
// the edge itself perfectly crisp.
const edgeFadeGLSL = `
  float edgeFade(vec3 n, vec3 viewDir) {
    float fresnel = abs(dot(normalize(n), normalize(viewDir)));
    return smoothstep(0.0, 0.28, fresnel);
  }
`;

const coneVertexShader = `
  attribute vec3 color;
  varying vec3 vColor;
  varying vec3 vLocalPos;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying float vSafeAtt;

  ${SAFE_ZONE_VERTEX_CHUNK}

  void main() {
    vColor = color;
    vLocalPos = position;
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mvPosition.xyz);
    gl_Position = projectionMatrix * mvPosition;
    // A beam crossing the cover is dimmed, never cut - see the addendum's
    // safe-zone rule and shaders/safeZone.glsl.js.
    vSafeAtt = safeZoneAttenuation(gl_Position);
  }
`;

const coneFragmentShader = `
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uTime;
  varying vec3 vColor;
  varying vec3 vLocalPos;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying float vSafeAtt;

  ${noiseGLSL}
  ${edgeFadeGLSL}

  void main() {
    float n = noise3D(vLocalPos * 0.9 + vec3(0.0, uTime * 0.5, 0.0));
    float density = clamp(0.45 + n * 1.2, 0.0, 1.6); // patchy - never fully solid, never fully gone
    float alpha = vColor.r * density * uOpacity * edgeFade(vNormal, vViewDir) * vSafeAtt;
    gl_FragColor = vec4(uColor, alpha);
  }
`;

const haloVertexShader = `
  attribute vec3 color;
  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying float vSafeAtt;

  ${SAFE_ZONE_VERTEX_CHUNK}

  void main() {
    vColor = color;
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mvPosition.xyz);
    gl_Position = projectionMatrix * mvPosition;
    vSafeAtt = safeZoneAttenuation(gl_Position);
  }
`;

const haloFragmentShader = `
  uniform vec3 uColor;
  uniform float uOpacity;
  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying float vSafeAtt;

  ${edgeFadeGLSL}

  void main() {
    float alpha = vColor.r * uOpacity * edgeFade(vNormal, vViewDir) * vSafeAtt;
    gl_FragColor = vec4(uColor, alpha);
  }
`;

const DOWN = new THREE.Vector3(0, -1, 0);
const _dir = new THREE.Vector3();
const _quat = new THREE.Quaternion();

class LightHead {
  constructor(index, total, mountSlots) {
    this.index = index;
    this._mountSlots = mountSlots;
    // Initial slots spread evenly across the pool (index-based) so the
    // starting arrangement still looks deliberate, not clustered by
    // chance - relocation during cuts is what adds the actual variety.
    this._mountSlotIndex = Math.floor((index / total) * mountSlots.length) % mountSlots.length;
    this.basePosition = mountSlots[this._mountSlotIndex].clone();

    this.pivot = new THREE.Group();
    this.pivot.position.copy(this.basePosition);

    // Fixed per-head "personality" - beam size, sweep speed, and how far
    // it tends to roam - so 5 heads don't all move through the same
    // statistical pattern (real rigs mix fixture types and speeds; this
    // fakes that cheaply via multipliers rather than modeling distinct
    // fixture types). Computed before the geometry below since
    // beamScale sizes it directly.
    this.beamScale = 0.7 + Math.random() * 0.8; // 0.7 (tight spot) - 1.5 (wide wash)
    this.speedMultiplier = 0.6 + Math.random() * 1.0; // 0.6 (lazy) - 1.6 (snappy)
    this.rangeBias = 0.55 + Math.random() * 0.75; // 0.55 (stays central) - 1.3 (roams wide)

    const coneGeometry = new THREE.ConeGeometry(0.55 * this.beamScale, CONE_HEIGHT, 24, 6, true);
    coneGeometry.translate(0, -CONE_HEIGHT / 2, 0); // apex at the pivot, cone points down -Y by default
    buildFadeColors(coneGeometry, CONE_HEIGHT);

    const coneMaterial = new THREE.ShaderMaterial({
      // safeZoneUniforms are shared holder objects (see safeZone.glsl.js) -
      // spreading them in means scene-manager's single resize write reaches
      // every cone and halo in the rig at once.
      uniforms: Object.assign({}, safeZoneUniforms, {
        uColor: { value: new THREE.Color(0xffffff) },
        uOpacity: { value: 0.16 },
        uTime: { value: 0 }
      }),
      vertexShader: coneVertexShader,
      fragmentShader: coneFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    this.cone = new THREE.Mesh(coneGeometry, coneMaterial);
    this.pivot.add(this.cone);

    // Soft blur around the beam, built from several thin, dimmer,
    // undisplaced copies of the same cone at growing radii rather than
    // one - a single halo shell reads as "a second, separate cone" (a
    // visible extra edge); stacking HALO_LAYER_COUNT of them at
    // decreasing opacity approximates a real gaussian falloff, so the
    // brightness fades continuously from the sharp core outward instead
    // of stepping down in one visible jump.
    this.halos = [];
    for (let i = 0; i < HALO_LAYER_COUNT; i++) {
      const t = (i + 1) / HALO_LAYER_COUNT;
      const radius = (0.55 + t * 0.85) * this.beamScale;
      const opacity = HALO_BASE_OPACITY * Math.pow(0.62, i);
      const haloGeometry = new THREE.ConeGeometry(radius, CONE_HEIGHT, 24, 1, true);
      haloGeometry.translate(0, -CONE_HEIGHT / 2, 0);
      buildFadeColors(haloGeometry, CONE_HEIGHT);
      const haloMaterial = new THREE.ShaderMaterial({
        uniforms: Object.assign({}, safeZoneUniforms, {
          uColor: { value: new THREE.Color(0xffffff) },
          uOpacity: { value: opacity }
        }),
        vertexShader: haloVertexShader,
        fragmentShader: haloFragmentShader,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide
      });
      const halo = new THREE.Mesh(haloGeometry, haloMaterial);
      halo.userData.baseOpacity = opacity;
      this.pivot.add(halo);
      this.halos.push(halo);
    }

    // Higher decay than the light's default so the floor hit itself
    // (separate from the cone mesh's own fade above) also dims quickly -
    // the two together mean nothing bright/hard-edged actually reaches
    // the ground.
    this.spot = new THREE.SpotLight(0xffffff, 8, 14, THREE.MathUtils.degToRad(16), 0.5, 2.4);
    this.spot.castShadow = false;
    // Fixed local point straight down, matching the cone's own authored
    // direction - aiming happens by rotating the whole pivot (see
    // setTarget below), not by moving this target around, so the visible
    // cone and the actual light direction always agree.
    this.spot.target.position.set(0, -6, 0);
    this.pivot.add(this.spot);
    this.pivot.add(this.spot.target);

    // per-head phase offset so colors don't lerp in lockstep - "jamais
    // statique" applies to the whole rig, not just one light
    this.colorPhaseOffset = Math.random() * Math.PI * 2;
    this.colorPairOffset = Math.floor(Math.random() * 4);
    this._tmpColor = new THREE.Color();
    // World-space point this head is currently aimed at - kept up to
    // date by setTarget() so other systems (ParticleStorm's reveal
    // shader) can read where the beams are without recomputing the
    // sweep math themselves.
    this.currentTarget = new THREE.Vector3();

    // Discrete cut cycle: hold a look, fade to black, jump to a new
    // anchor point while invisible, fade back in. See CUT_FADE_*_S above.
    this.fadeMul = 1;
    this._cutState = 'holding'; // 'holding' | 'fading-out' | 'fading-in'
    this._cutStateStartedAt = 0;
    this._nextCutAt = Math.random() * 3; // staggered first cut per head

    // Continuous gentle sweep *around* the current anchor - the cut cycle
    // alone left heads dead still between cuts, which read as "not
    // rotating anymore" compared to the pilot's original continuous
    // sweep. This restores real rotation without reintroducing the
    // original bug (a single rig-wide sweep with a short, exactly
    // repeating period): the wander is small and local to whatever
    // anchor the last cut picked, and every head has its own phase/
    // frequency, so there's no shared cadence to lock onto.
    this._anchorX = 0;
    this._anchorZ = 0;
    this._wanderPhaseX = Math.random() * Math.PI * 2;
    this._wanderPhaseZ = Math.random() * Math.PI * 2;
    this._wanderFreqX = 0.55 + Math.random() * 0.35;
    this._wanderFreqZ = 0.45 + Math.random() * 0.35;

    // 2-3 preferred spots this head alternates between on each cut
    // (with jitter so it's never pixel-identical), instead of a fresh
    // fully-random point every time. Fully random cuts still don't
    // repeat exactly (see the earlier "reset" fix) but they don't build
    // any recognizable pattern either - over a whole track that reads as
    // uniform noise rather than "interesting". Cycling a small set of
    // real spots gives the eye something to notice returning to.
    this.preferredZones = [];
    const zoneCount = 2 + Math.floor(Math.random() * 2);
    for (let z = 0; z < zoneCount; z++) {
      this.preferredZones.push({
        angle: Math.random() * Math.PI * 2,
        radiusFrac: 0.3 + Math.random() * 0.7
      });
    }
    this._nextZoneIndex = Math.floor(Math.random() * zoneCount);
  }

  // Rotates the pivot so its local "down" (where both the cone and the
  // spot's target are authored) points at the given world-space point.
  // Rotating the whole pivot - rather than only moving the SpotLight's
  // target - is what makes the *visible* cone mesh actually sweep;
  // moving just the light's target left the beam mesh pointing straight
  // down forever while only the (invisible) illumination direction moved.
  setTarget(x, y, z) {
    this.currentTarget.set(x, y, z);
    _dir.set(x, y, z).sub(this.basePosition).normalize();
    _quat.setFromUnitVectors(DOWN, _dir);
    this.pivot.quaternion.copy(_quat);
  }

  applyColor(palette, time) {
    const a = palette[this.colorPairOffset % palette.length];
    const b = palette[(this.colorPairOffset + 1) % palette.length];
    const t = (Math.sin(time * 0.15 + this.colorPhaseOffset) + 1) / 2;
    lerpHex(a, b, t, this._tmpColor);
    this.cone.material.uniforms.uColor.value.copy(this._tmpColor);
    this.halos.forEach((halo) => halo.material.uniforms.uColor.value.copy(this._tmpColor));
    this.spot.color.copy(this._tmpColor);
  }
}

export class StageLightRig {
  constructor() {
    this._heads = [];
    this._time = 0;
    this._lastTrackChangedAt = null;
    this._blackoutStartedAt = null;
    this._palette = DEFAULT_PALETTE.map((hex) => new THREE.Color(hex));
    // null = all heads active (normal show). A number isolates that one
    // head (by LightHead.index) so it can be worked on alone - see
    // setSolo() and party-mode-controller.js's head switcher.
    this._soloIndex = null;
    this._ambient = null;
    // Optional per-head aim override, set by whichever owner asks for it (see
    // StageRigRegistry). null = the historic behaviour: every head sweeps a
    // disc on the floor plane. Entries are { x:[min,max], z:[min,max], y }.
    this._aimPlan = null;
    // Set by the registry from the current owners' declared options -
    // see stageRig.acquire() at the bottom of this file.
    this._intensityScale = 1;
    // > 0 constrains every head's aim to at least this XZ radius, keeping
    // beams off the camera->artwork axis (zones: 'floor').
    this._minTargetRadius = 0;
    // Multiplies spot.intensity only - see setOwnerOptions.
    this._floorLightBoost = 1;
  }

  // Called by the registry whenever the set of owners changes.
  //
  // intensityScale (default 1.0) dims the whole rig for an owner that wants
  // it in the background: cone opacity, halo opacity AND spot intensity.
  //
  // floorLightBoost stays a SEPARATE multiplier on spot.intensity alone,
  // because wanting the rig quieter overall and wanting its beams to
  // actually reach the floor are independent needs - an owner can ask for
  // both at once (dim beams, but pools that still land). The two multiply.
  setOwnerOptions(intensityScale, minTargetRadius, floorLightBoost) {
    this._intensityScale = intensityScale;
    this._minTargetRadius = minTargetRadius;
    this._floorLightBoost = floorLightBoost;

    // The default decay 2.4 / distance 14 is tuned for beams read in mid-air,
    // where nothing has to survive the trip to the ground. An owner that
    // actually wants pools ON the floor needs the falloff relaxed, or the
    // light is divided by ~170 before it lands and the floor stays black.
    //
    // Keyed off floorLightBoost rather than the aim constraint: wanting lit
    // floor pools and wanting beams held off the centre axis are two
    // unrelated needs, and NeonPulse wants the first without the second.
    const wantsFloor = floorLightBoost > 1;
    this._heads.forEach((head) => {
      head.spot.decay = wantsFloor ? 1.1 : 2.4;
      head.spot.distance = wantsFloor ? 34 : 14;
    });
  }

  // index: a head index to isolate, or null to restore all heads.
  setSolo(index) {
    this._soloIndex = index;
  }

  init(scene) {
    this._group = new THREE.Group();
    const mountSlots = buildMountSlots(MOUNT_SLOT_COUNT);

    for (let i = 0; i < HEAD_COUNT; i++) {
      const head = new LightHead(i, HEAD_COUNT, mountSlots);
      this._group.add(head.pivot);
      this._heads.push(head);
    }

    // A simple haze plane rather than real volumetric fog - just enough
    // translucency for the additive cones to read as "beams in the air"
    // instead of flat translucent triangles.
    scene.fog = new THREE.FogExp2(0x000000, 0.045);

    scene.add(this._group);
    // Held so dispose() can actually remove it again. It used to be added
    // anonymously and never removed, which leaked one AmbientLight per
    // teardown into any scene that outlives the rig - invisible on a
    // top-level visualizer switch (SceneManager builds a fresh Scene each
    // time) but cumulative across AdaptiveParty's internal swaps, which
    // reuse one scene. The mandated leak test below catches exactly this.
    this._ambient = new THREE.AmbientLight(0x101018, 0.6);
    scene.add(this._ambient);
  }

  // profile.artworkPalette.vividColors (see artwork-palette.js) is the
  // artwork's dominant hue pushed to punchy saturation/lightness - the
  // raw dominantColors are too pastel (or, for B&W covers, too grayscale)
  // to read as "party" lighting on their own. Falls back to
  // DEFAULT_PALETTE until a palette is available.
  _resolvePalette(profile) {
    if (profile.artworkPalette && profile.artworkPalette.vividColors && profile.artworkPalette.vividColors.length >= 2) {
      return profile.artworkPalette.vividColors.map((hex) => new THREE.Color(hex));
    }
    return this._palette;
  }

  // 0..1: how wide/slow (0) vs fast/chaotic (1) the sweep should be right
  // now. Progress-driven for a real track; radio and anything without a
  // known duration (duration <= 0) just gets a steady, lively middle
  // value instead of being stuck at "slow intro" forever.
  _sweepIntensity(profile) {
    if (profile.duration <= 0) return 0.6;
    const p = profile.progress;
    if (p < 0.3) return THREE.MathUtils.mapLinear(p, 0, 0.3, 0.15, 0.7);
    if (p < 0.7) return 0.85 + Math.sin(p * 40) * 0.1; // chaotic middle section
    return THREE.MathUtils.mapLinear(p, 0.7, 1, 0.7, 0.2);
  }

  update(deltaTime, profile) {
    // artworkChangedAt, NOT trackChangedAt. The dip is meant to land with the
    // new cover, and trackChangedAt is stamped by the poll that first SAW the
    // new track - before its image has even been fetched. Keying off it dipped
    // the rig a download early, while the previous cover was still on screen.
    // Falls back to trackChangedAt so a cover that never decodes still gets
    // its dip rather than none at all.
    const changedAt = profile.artworkChangedAt != null
      ? profile.artworkChangedAt
      : profile.trackChangedAt;
    if (changedAt !== this._lastTrackChangedAt) {
      this._lastTrackChangedAt = changedAt;
      this._blackoutStartedAt = performance.now();
    }

    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) {
      this._time += deltaTime;
    }

    const palette = this._resolvePalette(profile);
    const intensity = this._sweepIntensity(profile);
    const sweepSpread = 5 - intensity * 1.7; // wide when slow, tighter/snappier when chaotic - scaled to RIG_RADIUS
    // Chaotic sections cut faster (shorter hold between cuts), slow
    // sections hold longer - the "slow -> chaotic -> slow" feel the brief
    // asked for, now expressed as cut cadence instead of drift speed.
    const cutIntervalMin = THREE.MathUtils.lerp(3, 0.8, intensity);
    const cutIntervalMax = THREE.MathUtils.lerp(6, 1.8, intensity);
    // Continuous wander speed/radius - faster and slightly wider when
    // chaotic, same "slow -> chaotic -> slow" language as everything else
    // here, just applied to the rotation instead of the cut cadence.
    const wanderSpeed = 0.3 + intensity * 1.1;
    const wanderRadius = sweepSpread * 0.22;

    const beatPulseMul = computeBeatPulse(profile.beatPhase, BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);

    let blackoutMul = 1;
    if (this._blackoutStartedAt !== null) {
      const elapsed = performance.now() - this._blackoutStartedAt;
      if (elapsed >= BLACKOUT_MS) {
        this._blackoutStartedAt = null;
      } else {
        const half = BLACKOUT_MS / 2;
        blackoutMul = elapsed < half ? 1 - elapsed / half : (elapsed - half) / half;
      }
    }

    this._heads.forEach((head) => {
      head.applyColor(palette, this._time);

      if (!paused) {
        this._advanceCut(head, sweepSpread, cutIntervalMin, cutIntervalMax);
      }

      // Always applied (even mid-fade) so a head keeps gently rotating
      // right up until - and immediately after - a cut, not just while
      // fully held. this._time itself freezes on pause, so this freezes
      // along with everything else without needing its own guard.
      // speedMultiplier/rangeBias are the per-head personality set in
      // the constructor - some heads sweep faster/wider than others.
      const headWanderRadius = wanderRadius * head.rangeBias;
      const headWanderSpeed = wanderSpeed * head.speedMultiplier;
      let wx = head._anchorX + Math.sin(this._time * headWanderSpeed * head._wanderFreqX + head._wanderPhaseX) * headWanderRadius;
      let wz = head._anchorZ + Math.cos(this._time * headWanderSpeed * head._wanderFreqZ + head._wanderPhaseZ) * headWanderRadius;
      // zones: 'floor' - push the aim point out past _minTargetRadius so no
      // head ever points down the camera->artwork axis. Applied to the final
      // target (after both the cut anchor and the wander), so it constrains
      // every path to the aim point rather than just where cuts land.
      if (this._minTargetRadius > 0) {
        const r = Math.sqrt(wx * wx + wz * wz);
        if (r < this._minTargetRadius) {
          // A head sitting exactly on the axis has no direction to be
          // pushed along; its own mount angle is the natural fallback.
          const angle = r > 0.0001 ? Math.atan2(wz, wx) : Math.atan2(head.basePosition.z, head.basePosition.x);
          wx = Math.cos(angle) * this._minTargetRadius;
          wz = Math.sin(angle) * this._minTargetRadius;
        }
      }
      // The plan is applied LAST, after the wander and the floor-zone radius
      // push, so nothing downstream can drag a planned head back out of its
      // volume. It is also the only path that aims off the y=0 floor plane.
      const plan = this._planFor(head.index);
      if (plan) {
        wx = THREE.MathUtils.clamp(wx, plan.x[0], plan.x[1]);
        wz = THREE.MathUtils.clamp(wz, plan.z[0], plan.z[1]);
        head.setTarget(wx, plan.y, wz);
      } else {
        head.setTarget(wx, 0, wz);
      }

      const soloMul = this._soloIndex === null || this._soloIndex === head.index ? 1 : 0;
      const scale = this._intensityScale;
      head.cone.material.uniforms.uOpacity.value = 0.16 * blackoutMul * head.fadeMul * soloMul * beatPulseMul * scale;
      head.cone.material.uniforms.uTime.value = this._time;
      head.halos.forEach((halo) => {
        halo.material.uniforms.uOpacity.value = halo.userData.baseOpacity * blackoutMul * head.fadeMul * soloMul * beatPulseMul * scale;
      });
      head.spot.intensity = 8 * blackoutMul * head.fadeMul * soloMul * beatPulseMul * scale * this._floorLightBoost;
    });
  }

  // Drives one head's hold -> fade-out -> jump -> fade-in -> hold cycle.
  // Freezing this entirely when paused (see the `!paused` guard at the
  // call site) means a head paused mid-fade just holds that exact
  // brightness rather than snapping to a state - "freeze, don't
  // extinguish" applies here same as the track-change blackout.
  _advanceCut(head, sweepSpread, cutIntervalMin, cutIntervalMax) {
    const t = this._time;

    if (head._cutState === 'holding') {
      if (t >= head._nextCutAt) {
        head._cutState = 'fading-out';
        head._cutStateStartedAt = t;
      }
      return;
    }

    if (head._cutState === 'fading-out') {
      const elapsed = t - head._cutStateStartedAt;
      head.fadeMul = Math.max(0, 1 - elapsed / CUT_FADE_OUT_S);
      if (elapsed >= CUT_FADE_OUT_S) {
        // Fully dark now - the jump itself is never seen. Sometimes also
        // relocate the head's actual mount point (not just its aim) to a
        // different slot from the wider pool - this is what makes the
        // beam's visible *origin* actually move around instead of being
        // pinned at one of only HEAD_COUNT fixed points forever.
        if (head._mountSlots.length > 1 && Math.random() < MOUNT_RELOCATE_CHANCE) {
          let newIndex = head._mountSlotIndex;
          while (newIndex === head._mountSlotIndex) {
            newIndex = Math.floor(Math.random() * head._mountSlots.length);
          }
          head._mountSlotIndex = newIndex;
          head.basePosition.copy(head._mountSlots[newIndex]);
          head.pivot.position.copy(head.basePosition);
        }

        // The jump itself is never seen, only the fade out and the light
        // reappearing somewhere new. Cycles through this head's own
        // preferred zones (with jitter) rather than a fresh fully-random
        // point, and scales by rangeBias so "roams wide" heads actually
        // range further than "stays central" ones. This moves the anchor
        // the continuous wander (see update()) sweeps around, not the
        // final on-screen target directly.
        const plan = this._planFor(head.index);
        if (plan) {
          // A planned head still cuts and re-picks, but always inside its own
          // volume, so "two heads are on the crowd" holds at every instant
          // rather than on average.
          head._anchorX = plan.x[0] + Math.random() * (plan.x[1] - plan.x[0]);
          head._anchorZ = plan.z[0] + Math.random() * (plan.z[1] - plan.z[0]);
        } else {
          const zone = head.preferredZones[head._nextZoneIndex];
          head._nextZoneIndex = (head._nextZoneIndex + 1) % head.preferredZones.length;
          const angle = zone.angle + (Math.random() - 0.5) * 0.5;
          const radiusFrac = THREE.MathUtils.clamp(zone.radiusFrac + (Math.random() - 0.5) * 0.15, 0.1, 1);
          const radius = radiusFrac * sweepSpread * head.rangeBias;
          head._anchorX = Math.cos(angle) * radius;
          head._anchorZ = Math.sin(angle) * radius;
        }
        head._cutState = 'fading-in';
        head._cutStateStartedAt = t;
      }
      return;
    }

    // fading-in
    const elapsed = t - head._cutStateStartedAt;
    head.fadeMul = Math.min(1, elapsed / CUT_FADE_IN_S);
    if (elapsed >= CUT_FADE_IN_S) {
      head.fadeMul = 1;
      head._cutState = 'holding';
      // Divided by speedMultiplier - a "snappy" head (>1) cuts sooner
      // than a "lazy" one (<1), on top of the shared progress-driven
      // slow/chaotic pacing.
      head._nextCutAt = t + (cutIntervalMin + Math.random() * (cutIntervalMax - cutIntervalMin)) / head.speedMultiplier;
    }
  }

  // Pins specific heads to specific volumes instead of the shared floor disc.
  // Only the owner that asks for it is affected; passing null restores the
  // default sweep for everyone.
  setAimPlan(plan) {
    this._aimPlan = plan && plan.length ? plan : null;
  }

  _planFor(index) {
    if (!this._aimPlan) return null;
    return this._aimPlan[index] || null;
  }

  // Current world-space aim point of each head, in stable head-index
  // order - used by ParticleStorm to reveal particles the beams are
  // actually passing over.
  getTargetPositions() {
    return this._heads.map((head) => head.currentTarget);
  }

  dispose(scene) {
    this._heads.forEach((head) => {
      head.cone.geometry.dispose();
      head.cone.material.dispose();
      head.halos.forEach((halo) => {
        halo.geometry.dispose();
        halo.material.dispose();
      });
    });
    scene.remove(this._group);
    if (this._ambient) {
      scene.remove(this._ambient);
      this._ambient = null;
    }
    // Without this a re-init would stack a second set of heads onto the
    // first - harmless while every rig was single-use, not once the
    // registry below can create/destroy one repeatedly.
    this._heads = [];
    this._group = null;
    scene.fog = null;
  }
}

// How far out a 'floor' owner's beams are held from the camera->artwork
// axis. Half RIG_RADIUS puts every aim point well outside the cover.
const FLOOR_ZONE_MIN_RADIUS = RIG_RADIUS * 0.5;

// The rig is a SHARED scene object: at most one instance may exist at a
// time, even when AdaptiveParty composes two visualizers that both want
// one. Ownership is reference-counted here rather than left to whoever
// happens to construct a StageLightRig.
//
// Each visualizer declares `RIG` and its desired intensityScale at the top
// of its own file, then acquires/releases around its own init/dispose.
// When two owners overlap, the MOST RESTRICTIVE settings win: the minimum
// intensityScale, and 'floor' zones if any owner asks for them.
class StageRigRegistry {
  constructor() {
    this._owners = new Map();
    this._rig = null;
    this._scene = null;
    this._primaryOwner = null;
  }

  // options: { scene, intensityScale = 1, zones = 'default' }
  //
  // `scene` is not in the addendum's signature but is unavoidable: the rig
  // has to be added to a specific THREE.Scene, and SceneManager builds a
  // brand new one on every top-level visualizer switch.
  acquire(ownerId, options) {
    const opts = options || {};
    const scene = opts.scene;
    if (!scene) throw new Error('stageRig.acquire needs options.scene');

    // A different scene means SceneManager swapped underneath us; rebuild
    // rather than leaving the rig parented to a dead scene. Owners recorded
    // against that dead scene go with it - keeping them would hold the
    // refcount above zero forever, so the rig would never be released again.
    if (this._rig && this._scene !== scene) {
      this._rig.dispose(this._scene);
      this._rig = null;
      this._scene = null;
      this._owners.clear();
      this._primaryOwner = null;
    }
    if (!this._rig) {
      this._rig = new StageLightRig();
      this._rig.init(scene);
      this._scene = scene;
    }

    this._owners.set(ownerId, {
      intensityScale: opts.intensityScale != null ? opts.intensityScale : 1,
      zones: opts.zones || 'default',
      floorLightBoost: opts.floorLightBoost != null ? opts.floorLightBoost : 1,
      aimPlan: opts.aimPlan || null
    });
    if (this._primaryOwner === null) this._primaryOwner = ownerId;
    this._applyOwnerOptions();
    return this._rig;
  }

  release(ownerId) {
    if (!this._owners.has(ownerId)) return;
    this._owners.delete(ownerId);
    if (this._primaryOwner === ownerId) {
      const next = this._owners.keys().next();
      this._primaryOwner = next.done ? null : next.value;
    }
    if (this._owners.size === 0) {
      if (this._rig) this._rig.dispose(this._scene);
      this._rig = null;
      this._scene = null;
      this._primaryOwner = null;
      return;
    }
    this._applyOwnerOptions();
  }

  // Only the primary owner actually advances the rig. Two composed
  // visualizers both calling update() would otherwise advance its internal
  // clock twice per frame, doubling every cut/wander/colour speed.
  update(deltaTime, profile, ownerId) {
    if (this._rig && ownerId === this._primaryOwner) this._rig.update(deltaTime, profile);
  }

  get() {
    return this._rig;
  }

  _applyOwnerOptions() {
    if (!this._rig) return;
    let scale = 1;
    let minRadius = 0;
    let boost = 1;
    // Unlike the other options there is no sensible way to merge two aim
    // plans, so the first owner that supplies one wins and the rest keep the
    // default sweep. In practice only one visualizer at a time asks.
    let aimPlan = null;
    this._owners.forEach((o) => {
      scale = Math.min(scale, o.intensityScale);
      if (o.zones === 'floor') minRadius = Math.max(minRadius, FLOOR_ZONE_MIN_RADIUS);
      boost = Math.max(boost, o.floorLightBoost);
      if (aimPlan === null && o.aimPlan) aimPlan = o.aimPlan;
    });
    this._rig.setOwnerOptions(scale, minRadius, boost);
    this._rig.setAimPlan(aimPlan);
  }

  // Test hook (see the leak test) - how many owners currently hold the rig.
  get ownerCount() {
    return this._owners.size;
  }
}

export const stageRig = new StageRigRegistry();
